import { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../css/Profile.css";
import ProfileIcon from "../icons/ProfileIcon";
import ChangeAccountDetailsModal from "../ModalComponents/ChangeAccountDetailsModal";
import QRCode from "../QRCode";
import { DarkModeTheme, LightModeTheme, onThemeChange } from "./Themes";
import CustomThemeCreator from "./CustomTheme";
import { useSelector } from "react-redux";

/**
 * The Profile component represents the restaurant's profile page.
 * It includes the profile header with a picture, name, and action buttons (edit and logout),
 * as well as sections for appearance settings, security settings, and QR code handling.
 *
 * @returns {JSX.Element} The Profile page JSX.
 */
const Profile = () => {
  const { restaurantId } = useParams();

  const isMobile = useSelector((state) => state.mobile.isMobile);

  const canvasWidth = isMobile ? 250 : 400;
  const canvasHeight = isMobile ? 300 : 520;
  const qrCodeSize = isMobile ? 150 : 300;

  // variables for changing account details
  const accountDetails = ["name", "email", "password"];
  const [isChangeAccountDetailsOpen, setIsChangeAccountDetailsOpen] =
    useState(false);
  const openChangeAccountDetails = useCallback(
    () => setIsChangeAccountDetailsOpen(true),
    [],
  );
  const closeChangeAccountDetails = useCallback(
    () => setIsChangeAccountDetailsOpen(false),
    [],
  );
  const [detailToChange, setDetailToChange] = useState("");

  const handleAccountChange = (detail) => {
    setDetailToChange(detail);
    openChangeAccountDetails();
  };

  const restaurant = JSON.parse(localStorage.getItem("restaurant"));
  const currentTheme = restaurant?.theme;

  const navigate = useNavigate();
  const [customThemeOpen, setCustomThemeOpen] = useState(false);
  const [themeData, setThemeData] = useState(
    JSON.parse(currentTheme.themeString),
  );

  const handleThemeDataChange = useCallback((newThemeData) => {
    setThemeData(newThemeData);
  }, []);

  if (!restaurant) {
    // If restaurant data is not available, redirect to the login page
    navigate("/");
    return null;
  }
  const restaurantName = restaurant.name;
  const restaurantURL =
    process.env.REACT_APP_URL_FRONTEND + `/menu/${restaurant.menu.id}`;

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("restaurant");
    navigate("/");
  };

  const changeTheme = (theme) => {
    setCustomThemeOpen(false);
    onThemeChange(theme);
  };

  const onCustomThemeClick = () => {
    if (customThemeOpen) {
      onThemeChange({
        themeType: "CUSTOM",
        themeString: JSON.stringify(themeData),
      });
    }
    setCustomThemeOpen(!customThemeOpen);
  };

  const resetThemeData = () => {
    setThemeData(JSON.parse(LightModeTheme.themeString));
    onThemeChange(LightModeTheme);
  };

  return (
    <div className="profile-container">
      <div className="Profile">
        <div className="profile-header">
          <div className="profile-picture">
            <ProfileIcon />
          </div>
          <div className="profile-name">
            <h2 className="profile-text greeting">hello, {restaurantName}!</h2>
            <div className="action-buttons">
              <button className="logout-button" onClick={handleLogout}>
                log out
              </button>
            </div>
          </div>
        </div>
        <h1 className="account-text profile-text">
          account
          <div className="account-buttons">
            {accountDetails.map((detail) => (
              <button
                key={detail}
                className="profile-button"
                onClick={() => handleAccountChange(detail)}
              >
                change {detail}
              </button>
            ))}
          </div>
        </h1>
        <h1 className="appearance-text profile-text">
          appearance
          <div className="appearence-buttons">
            <button
              className="light-mode-button profile-button"
              onClick={() => changeTheme(LightModeTheme)}
            >
              light mode
            </button>
            <button
              className="dark-mode-button profile-button"
              onClick={() => changeTheme(DarkModeTheme)}
            >
              dark mode
            </button>
            {/* <button className="system-theme-mode-button profile-button">
              system theme
            </button> */}
            <button
              className="custom-theme-mode-button profile-button"
              onClick={onCustomThemeClick}
            >
              {customThemeOpen ? "save custom theme" : "create custom theme"}
            </button>
            <button
              className="profile-button custom-theme-mode-button"
              onClick={resetThemeData}
            >
              reset custom theme
            </button>
          </div>
          {customThemeOpen && (
            <CustomThemeCreator
              themeData={themeData}
              handleThemeDataChange={handleThemeDataChange}
            />
          )}
        </h1>
        <h1 className="qr-code-text profile-text">
          qr code
          <div className="qr-label">
            <QRCode
              className="qr-code"
              restaurantName={restaurantName}
              restaurantURL={restaurantURL}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              qrCodeSize={qrCodeSize}
            />
          </div>
        </h1>
        {isChangeAccountDetailsOpen && (
          <ChangeAccountDetailsModal
            className="Profile-ChangeAccountDetailsModal"
            isOpen={isChangeAccountDetailsOpen}
            onRequestClose={closeChangeAccountDetails}
            width="50%"
            restaurantId={restaurantId}
            detailToChange={detailToChange}
          />
        )}
      </div>
    </div>
  );
};

export default Profile;
